import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, Tag, ArrowRight } from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { TravellerSuggest, ContactDatalists, useSavedContacts, refreshSavedTravellers } from "@/components/TravellerSuggest";
import { formatINR } from "@/mock";
import { apiCartCheckout, apiCreateRzpOrder, apiVerifyRzp } from "@/api";
import { HotelCrossSell } from "@/components/CrossSell";
import { loadRazorpay } from "@/lib/razorpay";
import { downloadReceiptPdf } from "@/lib/invoicePdf";
import { toast } from "sonner";

const CartCheckout = () => {
  const { items, subtotal, discount, total, combo, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (user) setContact({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  }, [user]);

  useEffect(() => {
    if (!result && items.length === 0) navigate("/cart");
  }, [items.length, result, navigate]);

  const pay = async () => {
    if (!user) {
      sessionStorage.setItem("ft_after_login", "/cart-checkout");
      toast.info("Please log in to complete your booking");
      navigate("/login");
      return;
    }
    if (!contact.name || !contact.email || !contact.phone) { toast.error("Please fill contact details"); return; }
    const payload = {
      items: items.map((i) => ({ kind: i.kind, title: i.title, route: i.route || i.title, date: i.date || "", price: Number(i.price), pax: i.pax || 1, meta: i.meta || null })),
      contact,
    };
    setBusy(true);
    try {
      // Agents pay from wallet; customers pay via Razorpay
      if (user.role === "agent") {
        const res = await apiCartCheckout(payload);
        setResult(res); clear(); refreshSavedTravellers(); toast.success("Trip booked from wallet!");
        return;
      }
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway. Try again."); setBusy(false); return; }
      const order = await apiCreateRzpOrder(payload);
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "FLYTRIP",
        description: `Trip booking (${items.length} item${items.length > 1 ? "s" : ""})`,
        prefill: { name: contact.name, email: contact.email, contact: contact.phone },
        theme: { color: "#1B1F5B" },
        handler: async (resp) => {
          try {
            const res = await apiVerifyRzp({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            setResult(res); clear(); refreshSavedTravellers(); toast.success("Payment successful — trip booked!");
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed. If money was deducted, contact support.");
          } finally { setBusy(false); }
        },
        modal: { ondismiss: () => { setBusy(false); toast.info("Payment cancelled"); } },
      });
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed"); setBusy(false); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setBusy(false);
    }
  };

  if (result) {
    const flightOnly = result.bookings.some((b) => b.type === "flight") && !result.bookings.some((b) => b.type === "hotel");
    const flightCity = result.bookings.find((b) => b.type === "flight")?.route?.split("->").pop()?.trim();
    return (
      <div className="min-h-screen bg-[#F5F7FB]">
        <TopBar /><Header />
        <div className="mx-auto max-w-3xl px-4 py-10">
          <div className="rounded-3xl border border-emerald-100 bg-white p-8 text-center shadow-sm" data-testid="cart-success">
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
            <h1 className="mt-3 font-[Poppins] text-2xl font-extrabold text-slate-800">Trip Confirmed!</h1>
            <p className="mt-1 text-sm text-slate-500">{result.bookings.length} item(s) booked · Total paid {formatINR(result.total)}{result.discount ? ` (saved ${formatINR(result.discount)})` : ""}</p>
            <div className="mt-5 space-y-2 text-left">
              {result.bookings.map((b) => (
                <div key={b.pnr} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 text-sm">
                  <span className="font-bold capitalize text-slate-700">{b.type} · {b.route}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-slate-500">PNR <b className="text-[#1B1F5B]">{b.pnr}</b></span>
                    <button data-testid={`receipt-${b.pnr}`} onClick={() => downloadReceiptPdf(b, { total: b.total })}
                      className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-bold text-slate-600 hover:border-[#1B1F5B]">Receipt</button>
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-center gap-2">
              <Link to="/dashboard" className="rounded-full bg-[#1B1F5B] px-6 py-2.5 text-xs font-extrabold text-white">View My Bookings</Link>
              <Link to="/" className="rounded-full border border-slate-200 px-6 py-2.5 text-xs font-extrabold text-slate-600">Back to Home</Link>
            </div>
          </div>
          {flightOnly && flightCity && (
            <HotelCrossSell city={flightCity} checkIn={result.bookings.find((b) => b.type === "flight")?.date} adults={1} />
          )}
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar /><Header />
      <div className="mx-auto max-w-4xl px-4 py-8">
        <h1 className="font-[Poppins] text-2xl font-extrabold text-[#1B1F5B]">Checkout</h1>
        <div className="mt-6 grid gap-6 lg:grid-cols-[1fr,340px]">
          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="text-base font-extrabold text-slate-800">Contact Details</h3>
              <p className="text-xs text-slate-400">Booking confirmation will be sent here.</p>
              <div className="mt-3 space-y-3">
                <TravellerSuggest testId="checkout-name" value={contact.name} placeholder="Full name" onChange={(v) => setContact({ ...contact, name: v })}
                  onPick={(t) => setContact({ name: `${t.firstName} ${t.lastName}`, email: t.email || contact.email, phone: t.phone || contact.phone })}
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
                <ContactDatalists />
                <input data-testid="checkout-email" list={user?.role === "agent" ? "saved-emails" : undefined} value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="Email" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
                <input data-testid="checkout-phone" list={user?.role === "agent" ? "saved-phones" : undefined} value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="Mobile number" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-[#1B1F5B]" />
              </div>
            </div>
            <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <h3 className="text-base font-extrabold text-slate-800">Payment</h3>
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-4 w-4 text-emerald-500" /> {user?.role === "agent" ? "Paid from your agent wallet balance" : "Secure payment via Razorpay — UPI, Cards, NetBanking & Wallets"}</p>
            </div>
          </div>

          <div className="h-fit rounded-2xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="checkout-summary">
            <h3 className="text-base font-extrabold text-slate-800">Order Summary</h3>
            <div className="mt-3 space-y-2 text-sm">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between text-slate-500"><span className="truncate pr-2 capitalize">{i.kind}: {i.title}</span><span>{formatINR(i.price)}</span></div>
              ))}
              <div className="flex justify-between border-t pt-2 text-slate-500"><span>Subtotal</span><span>{formatINR(subtotal)}</span></div>
              {combo && <div className="flex justify-between text-emerald-600"><span className="flex items-center gap-1"><Tag className="h-3.5 w-3.5" /> Combo 10%</span><span>-{formatINR(discount)}</span></div>}
              <div className="flex justify-between border-t border-dashed pt-2 text-base font-extrabold text-[#1B1F5B]"><span>Total</span><span>{formatINR(total)}</span></div>
            </div>
            <button data-testid="checkout-pay-btn" disabled={busy} onClick={pay}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#F5B32B] py-3 text-sm font-extrabold text-[#1B1F5B] hover:bg-[#e0a41f] disabled:opacity-60">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Booking...</> : <>Pay {formatINR(total)} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CartCheckout;
